import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useWebSocket } from '@/contexts/WebSocketContext';
import { useTheme } from '@/contexts/ThemeContext';
import { getScheduleThisWeek, getScheduleMembers, deleteSchedule, cancelEnrollment, deleteScheduleMember, Schedule, ScheduleMember } from '@/api/scheduleApi';
import { getMyCharacters, Character } from '@/api/characterApi';
import { getWeekRange, getInitialWeekOffset } from '@/utils/dateUtils';
import { getRaidAccentStyle } from '@/constants/raids';
import { useToast } from '@/components/ui/use-toast';
import { wsService } from '@/lib/websocket';
import { preloadClassIcons } from '@/utils/imagePreload';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { ChevronLeft, ChevronRight, Users, Clock, User, Shield, Swords, Plus, BarChart3, Settings, Menu, Edit, Trash2, X, Moon, Sun, Calendar, CalendarPlus } from 'lucide-react';
import { UserProfileDialog } from '@/components/UserProfileDialog';
import RankingPopover from '@/components/RankingPopover';
import ScheduleCreateModal from '@/components/ScheduleCreateModal';
import ScheduleEditModal from '@/components/ScheduleEditModal';
import ScheduleEnrollModal from '@/components/ScheduleEnrollModal';
import MyParticipationDialog from '@/components/MyParticipationDialog';
import ClassIcon from '@/components/ClassIcon';

export default function SchedulePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isConnected, activeUsers, userList } = useWebSocket();
  const { toast } = useToast();
  const { theme, setTheme } = useTheme();
  const isAdmin = user?.authorities?.includes('ADMIN');

  // 주 오프셋 (0 = 이번주, -1 = 지난주, 1 = 다음주)
  const [weekOffset, setWeekOffset] = useState(getInitialWeekOffset());

  // 현재 주의 스케줄
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [loading, setLoading] = useState(true);

  // 모바일: 선택된 날짜
  const [selectedDate, setSelectedDate] = useState<string>('');

  // 스케줄 상세 Dialog
  const [selectedSchedule, setSelectedSchedule] = useState<Schedule | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [scheduleMembers, setScheduleMembers] = useState<ScheduleMember[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);

  // 스케줄 생성 Modal
  const [createModalOpen, setCreateModalOpen] = useState(false);

  // 스케줄 수정 Modal
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editScheduleId, setEditScheduleId] = useState<number | null>(null);

  // 스케줄 참여 Modal
  const [enrollModalOpen, setEnrollModalOpen] = useState(false);
  const [isEnrollUpdate, setIsEnrollUpdate] = useState(false);

  // 내 참여 현황 Dialog
  const [participationDialogOpen, setParticipationDialogOpen] = useState(false);

  // 내 정보 Dialog (Desktop)
  const [userProfileDialogOpen, setUserProfileDialogOpen] = useState(false);

  // 캐릭터 데이터 (한 번만 로드)
  const [characters, setCharacters] = useState<Character[]>([]);
  const [charactersLoading, setCharactersLoading] = useState(true);

  // 현재 주의 날짜 범위
  const weekRange = getWeekRange(weekOffset);
  const { wedDate, tueDate, dates } = weekRange;

  /**
   * 주간 스케줄 로드
   */
  const loadSchedules = async (silent = false) => {
    try {
      if (!silent) {
        setLoading(true);
      }
      const data = await getScheduleThisWeek(wedDate, tueDate);
      setSchedules(data || []);

      // 모바일: 기본 선택 날짜 (오늘 또는 첫 번째 날짜)
      // 주가 바뀌면 선택된 날짜도 새 주의 날짜로 갱신
      if (dates && dates.length > 0) {
        const today = new Date().toISOString().split('T')[0];
        // selectedDate가 없거나 현재 주에 포함되지 않으면 갱신
        if (!selectedDate || !dates.includes(selectedDate)) {
          setSelectedDate(dates.includes(today) ? today : dates[0]);
        }
      }

      return data || [];
    } catch (error) {
      console.error('스케줄 로드 실패:', error);
      toast({
        title: '오류',
        description: '스케줄을 불러오는데 실패했습니다.',
        variant: 'destructive',
      });
      return [];
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    loadSchedules();
  }, [weekOffset]);

  /**
   * 캐릭터 데이터 로드 (한 번만)
   */
  useEffect(() => {
    const loadCharacters = async () => {
      try {
        const data = await getMyCharacters();
        // 레벨 높은 순으로 정렬
        const sortedData = data.sort((a, b) => {
          const levelA = parseFloat(a.itemLevel.replace(',', ''));
          const levelB = parseFloat(b.itemLevel.replace(',', ''));
          return levelB - levelA;
        });
        setCharacters(sortedData);

        // 내 캐릭터의 클래스 아이콘 프리로드
        const myClassNames = sortedData.map(char => char.characterClass);
        preloadClassIcons(myClassNames);
      } catch (error) {
        console.error('캐릭터 로드 실패:', error);
      } finally {
        setCharactersLoading(false);
      }
    };

    loadCharacters();
  }, []); // 마운트 시 한 번만 실행

  /**
   * 스케줄 멤버의 클래스 아이콘 프리로드
   * Note: Schedule 타입에는 참여자 정보가 포함되지 않아 별도 API 호출 필요
   * 참여자 아이콘은 상세 다이얼로그 열 때 로드됨
   */
  // useEffect(() => {
  //   if (schedules.length === 0) return;

  //   // 모든 스케줄에 참여한 캐릭터들의 클래스명 추출
  //   const allClassNames = new Set<string>();
  //   schedules.forEach(schedule => {
  //     if (schedule.attackers) {
  //       schedule.attackers.forEach(member => allClassNames.add(member.characterClass));
  //     }
  //     if (schedule.supporters) {
  //       schedule.supporters.forEach(member => allClassNames.add(member.characterClass));
  //     }
  //   });

  //   // 프리로드
  //   if (allClassNames.size > 0) {
  //     preloadClassIcons(Array.from(allClassNames));
  //   }
  // }, [schedules]); // 스케줄이 변경될 때마다 실행

  /**
   * WebSocket 알림 구독
   */
  useEffect(() => {
    if (!isConnected) return;

    const subscription = wsService.subscribeToNotifications((notification) => {

      const { type, raidTitle, nickname, characterName } = notification;
      const isMyAction = nickname === user?.memberNickname;

      // 자신의 행동은 알림 표시하지 않음
      if (isMyAction) return;

      let title = '';
      let description = '';

      switch (type) {
        case 'created':
          title = '새 일정 생성';
          description = `${nickname}님이 "${raidTitle}" 일정을 생성했습니다.`;
          break;
        case 'deleted':
          title = '일정 삭제';
          description = `${nickname}님이 "${raidTitle}" 일정을 삭제했습니다.`;
          break;
        case 'enrolled':
          title = '참여 알림';
          description = `${nickname}님이 "${raidTitle}"에 ${characterName}(으)로 참여했습니다.`;
          break;
        case 'cancelled':
          title = '참여 취소';
          description = `${nickname}님이 "${raidTitle}" 참여를 취소했습니다.`;
          break;
      }

      toast({
        title,
        description,
      });

      // 스케줄 목록 새로고침 (조용히 - 로딩 화면 없이)
      loadSchedules(true);
    });

    return () => {
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, [isConnected, user, toast]);

  /**
   * 스케줄 선택 시 참여자 목록 로드
   */
  useEffect(() => {
    if (selectedSchedule && dialogOpen) {
      loadScheduleMembers(selectedSchedule.scheduleId);
    }
  }, [selectedSchedule, dialogOpen]);

  /**
   * 스케줄 참여자 로드
   */
  const loadScheduleMembers = async (scheduleId: number) => {
    try {
      setLoadingMembers(true);
      const members = await getScheduleMembers(scheduleId);
      setScheduleMembers(members);
    } catch (error) {
      console.error('참여자 로드 실패:', error);
      setScheduleMembers([]);
    } finally {
      setLoadingMembers(false);
    }
  };

  /**
   * 주 네비게이션
   */
  const handlePrevWeek = () => setWeekOffset((prev) => prev - 1);
  const handleThisWeek = () => setWeekOffset(getInitialWeekOffset());
  const handleNextWeek = () => setWeekOffset((prev) => prev + 1);

  /**
   * 날짜별 스케줄 필터링
   */
  const getSchedulesForDate = (dateStr: string): Schedule[] => {
    if (!schedules || schedules.length === 0) return [];
    return schedules.filter((schedule) => {
      const scheduleDate = schedule.startTime.split('T')[0];
      return scheduleDate === dateStr;
    });
  };

  /**
   * 스케줄 카드 클릭
   */
  const handleScheduleClick = (schedule: Schedule) => {
    setSelectedSchedule(schedule);
    setDialogOpen(true);
  };

  /**
   * 스케줄 삭제
   */
  const handleDelete = async () => {
    if (!selectedSchedule) return;

    if (!window.confirm('정말로 이 스케줄을 삭제하시겠습니까?')) {
      return;
    }

    try {
      await deleteSchedule(selectedSchedule.scheduleId);
      toast({
        title: '성공',
        description: '스케줄이 삭제되었습니다.',
      });
      setDialogOpen(false);
      loadSchedules(true);
    } catch (error) {
      console.error('삭제 실패:', error);
      toast({
        title: '오류',
        description: '스케줄 삭제에 실패했습니다.',
        variant: 'destructive',
      });
    }
  };

  /**
   * 참여 취소
   */
  const handleCancelEnrollment = async () => {
    if (!selectedSchedule) return;

    if (!window.confirm('참여를 취소하시겠습니까?')) {
      return;
    }

    try {
      await cancelEnrollment(selectedSchedule.scheduleId);
      toast({
        title: '성공',
        description: '참여가 취소되었습니다.',
      });
      setDialogOpen(false);
      loadSchedules(true);
    } catch (error) {
      console.error('참여 취소 실패:', error);
      toast({
        title: '오류',
        description: '참여 취소에 실패했습니다.',
        variant: 'destructive',
      });
    }
  };

  /**
   * 캐릭터 변경
   */
  const handleChangeCharacter = () => {
    if (!selectedSchedule) return;

    if (!window.confirm('캐릭터를 변경하시겠습니까?')) {
      return;
    }

    setEnrollModalOpen(true);
    setIsEnrollUpdate(true);
  };

  /**
   * 참여자 강제 삭제 (Admin 전용)
   */
  const handleForceDelete = async (memberId: number) => {
    if (!window.confirm('이 참여자를 강제로 삭제하시겠습니까?')) {
      return;
    }

    try {
      await deleteScheduleMember(memberId);
      toast({
        title: '성공',
        description: '참여자가 삭제되었습니다.',
      });
      if (selectedSchedule) {
        loadScheduleMembers(selectedSchedule.scheduleId);
      }
    } catch (error) {
      console.error('강제 삭제 실패:', error);
      toast({
        title: '오류',
        description: '참여자 삭제에 실패했습니다.',
        variant: 'destructive',
      });
    }
  };

  /**
   * 날짜 포맷팅 (12/25 수)
   */
  const formatDateLabel = (dateStr: string): string => {
    const date = new Date(dateStr + 'T00:00:00');
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const weekday = ['일', '월', '화', '수', '목', '금', '토'][date.getDay()];
    return `${month}/${day} ${weekday}`;
  };

  /**
   * 시간 포맷팅 (21:00)
   */
  const formatTime = (datetime: string): string => {
    return datetime.split('T')[1]?.substring(0, 5) || '';
  };

  /**
   * 주 제목 (12/25 ~ 12/31)
   */
  const getWeekTitle = (): string => {
    if (!dates || dates.length === 0) return '로딩 중...';

    const start = formatDateLabel(dates[0]).split(' ')[0];
    const end = formatDateLabel(dates[6]).split(' ')[0];

    if (weekOffset === 0) return `이번주 (${start} ~ ${end})`;
    if (weekOffset === -1) return `지난주 (${start} ~ ${end})`;
    if (weekOffset === 1) return `다음주 (${start} ~ ${end})`;
    return `${start} ~ ${end}`;
  };

  /**
   * 스케줄 카드 렌더링
   */
  const renderScheduleCard = (schedule: Schedule) => {
    const isMySchedule = schedule.mySchedule === 1;
    const isEnrolled = schedule.isEnrolled === 1;
    const isDark = theme === 'dark';
    const accentStyle = getRaidAccentStyle(schedule.raidTitle, isDark);

    return (
      <div
        key={schedule.scheduleId}
        onClick={() => handleScheduleClick(schedule)}
        className="border rounded-lg p-2 mb-1.5 bg-card shadow-sm hover:shadow-md transition-shadow cursor-pointer"
        style={accentStyle}
      >
        {/* 레이드 타이틀 + MY 뱃지 */}
        <div className="flex items-center justify-between mb-1">
          <h4 className="font-bold text-sm truncate flex-1 mr-2">{schedule.raidTitle}</h4>
          {isMySchedule && (
            <Badge variant="secondary" className="text-xs px-1.5 py-0.5 flex-shrink-0">MY</Badge>
          )}
        </div>

        {/* 시간 */}
        <div className="flex items-center text-sm text-muted-foreground mb-1">
          <Clock className="w-4 h-4 mr-1.5" />
          {formatTime(schedule.startTime)}
        </div>

        {/* 인원 + 참여중 뱃지 */}
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center text-foreground">
            <Users className="w-4 h-4 mr-1.5" />
            {schedule.enrollMemberNum}/{schedule.atkNum + schedule.supNum}
          </div>
          {isEnrolled && (
            <Badge className="text-xs px-1.5 py-0.5 flex-shrink-0">참여중</Badge>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">로딩 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-20">
      {/* 헤더 */}
      <header className="bg-card shadow-sm sticky top-0 z-10 border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          {/* 사용자 정보 */}
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-bold">엘리시안 레이드 스케줄</h2>
              <p className="text-sm text-muted-foreground">{user?.memberNickname}님 환영합니다</p>
            </div>
            <div className="flex items-center gap-2">
              {/* Mobile & Desktop: 접속 인원 */}
              {isConnected && (
                <Popover>
                  <PopoverTrigger asChild>
                    <Badge variant="outline" className="cursor-pointer hover:bg-accent/50 transition-colors">
                      👥 {activeUsers}
                    </Badge>
                  </PopoverTrigger>
                  <PopoverContent className="w-64 p-3">
                    <h4 className="font-semibold text-sm mb-2">현재 접속 중인 사용자</h4>
                    {userList.length === 0 ? (
                      <p className="text-xs text-muted-foreground">접속자가 없습니다</p>
                    ) : (
                      <div className="space-y-1">
                        {userList.map((nickname, index) => (
                          <div
                            key={index}
                            className={`text-xs p-1.5 rounded ${
                              nickname === user?.memberNickname
                                ? 'bg-accent text-accent-foreground font-medium'
                                : 'text-muted-foreground'
                            }`}
                          >
                            {nickname} {nickname === user?.memberNickname && '(나)'}
                          </div>
                        ))}
                      </div>
                    )}
                  </PopoverContent>
                </Popover>
              )}

              {/* Mobile: 랭킹 아이콘 */}
              <div className="md:hidden">
                <RankingPopover>
                  <Button variant="ghost" size="icon">
                    <BarChart3 className="w-5 h-5" />
                  </Button>
                </RankingPopover>
              </div>

              {/* Desktop: 랭킹 + 통합 메뉴 */}
              <div className="hidden md:flex items-center gap-2">
                <RankingPopover>
                  <Button size="sm" variant="outline">
                    <BarChart3 className="w-4 h-4 mr-1" />
                    랭킹
                  </Button>
                </RankingPopover>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="sm" variant="outline">
                      <Menu className="w-4 h-4 mr-1" />
                      메뉴
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuItem onSelect={() => setUserProfileDialogOpen(true)}>
                      <User className="w-4 h-4 mr-2" />
                      내 정보
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => setParticipationDialogOpen(true)}>
                      <Calendar className="w-4 h-4 mr-2" />
                      내 참여 현황
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => setTheme(theme === 'dark' ? 'light' : 'dark')}>
                      {theme === 'dark' ? (
                        <>
                          <Sun className="w-4 h-4 mr-2" />
                          라이트 모드
                        </>
                      ) : (
                        <>
                          <Moon className="w-4 h-4 mr-2" />
                          다크 모드
                        </>
                      )}
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => setCreateModalOpen(true)}>
                      <Plus className="w-4 h-4 mr-2" />
                      일정 생성
                    </DropdownMenuItem>
                    {user?.authorities?.includes('ADMIN') && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onSelect={() => navigate('/admin')}>
                          <Settings className="w-4 h-4 mr-2" />
                          관리자
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              {/* Mobile: 메뉴 버튼 */}
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="md:hidden">
                    <Menu className="w-5 h-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent>
                  <SheetHeader>
                    <SheetTitle>메뉴</SheetTitle>
                    <SheetDescription className="sr-only">
                      사용자 설정 및 기능 메뉴
                    </SheetDescription>
                  </SheetHeader>
                  <div className="flex flex-col gap-3 mt-6">
                    <UserProfileDialog characters={characters}>
                      <Button variant="outline" className="w-full justify-start">
                        <User className="w-4 h-4 mr-2" />
                        내 정보
                      </Button>
                    </UserProfileDialog>
                    <Button
                      variant="outline"
                      className="w-full justify-start"
                      onClick={() => setParticipationDialogOpen(true)}
                    >
                      <Calendar className="w-4 h-4 mr-2" />
                      내 참여 현황
                    </Button>
                    <Button
                      variant="outline"
                      className="w-full justify-start"
                      onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
                    >
                      {theme === 'dark' ? (
                        <>
                          <Sun className="w-4 h-4 mr-2" />
                          라이트 모드
                        </>
                      ) : (
                        <>
                          <Moon className="w-4 h-4 mr-2" />
                          다크 모드
                        </>
                      )}
                    </Button>
                    <Button
                      onClick={() => setCreateModalOpen(true)}
                      className="w-full justify-start"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      일정 생성
                    </Button>
                    {user?.authorities?.includes('ADMIN') && (
                      <Button
                        variant="outline"
                        onClick={() => navigate('/admin')}
                        className="w-full justify-start"
                      >
                        <Settings className="w-4 h-4 mr-2" />
                        관리자
                      </Button>
                    )}
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>

          {/* 주 네비게이션 */}
          <div className="flex items-center justify-between">
            <Button variant="outline" size="sm" onClick={handlePrevWeek}>
              <ChevronLeft className="w-4 h-4" />
              이전주
            </Button>
            <div className="text-center">
              <h2 className="text-sm font-semibold">{getWeekTitle()}</h2>
              {weekOffset !== getInitialWeekOffset() && (
                <Button variant="link" size="sm" onClick={handleThisWeek} className="text-xs">
                  이번주로
                </Button>
              )}
            </div>
            <Button variant="outline" size="sm" onClick={handleNextWeek}>
              다음주
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* 메인 컨텐츠 */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Desktop: 7일 그리드 */}
        <div className="hidden md:grid md:grid-cols-7 gap-2">
          {dates.map((date) => {
            const daySchedules = getSchedulesForDate(date);
            const isToday = date === new Date().toISOString().split('T')[0];

            return (
              <div key={date} className={`bg-card rounded-lg shadow-sm overflow-hidden ${isToday ? 'border-2 border-primary' : 'border'}`}>
                {/* 날짜 헤더 */}
                <div className={`p-3 text-center border-b ${isToday ? 'bg-primary/15 border-primary text-primary font-bold' : 'bg-muted/50'}`}>
                  <div className="text-sm font-semibold">
                    {formatDateLabel(date)}
                  </div>
                  <div className={`text-sm mt-1 ${isToday ? 'text-primary/80' : 'text-muted-foreground'}`}>
                    {daySchedules.length}개 일정
                  </div>
                </div>

                {/* 스케줄 목록 */}
                <div className="p-2 min-h-[500px] max-h-[750px] overflow-y-auto">
                  {daySchedules.length === 0 ? (
                    <p className="text-sm text-muted-foreground/60 text-center py-4">일정 없음</p>
                  ) : (
                    daySchedules.map(renderScheduleCard)
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Mobile: 날짜 탭 + 선택된 날짜의 스케줄 */}
        <div className="md:hidden">
          <Tabs value={selectedDate} onValueChange={setSelectedDate}>
            {/* 날짜 탭 (가로 스크롤) */}
            <TabsList className="w-full justify-start overflow-x-auto flex-nowrap mb-4 h-auto">
              {dates.map((date) => {
                const daySchedules = getSchedulesForDate(date);
                const isToday = date === new Date().toISOString().split('T')[0];

                return (
                  <TabsTrigger
                    key={date}
                    value={date}
                    className={`flex-shrink-0 h-auto py-3 px-2 w-24 ${isToday ? 'bg-primary/15 border-2 border-primary text-primary data-[state=active]:bg-primary/20' : ''}`}
                  >
                    <div className="text-center w-full">
                      <div className={`text-sm whitespace-nowrap ${isToday ? 'font-bold' : 'font-semibold'}`}>
                        {formatDateLabel(date)}
                      </div>
                      <Badge variant={isToday ? 'default' : 'secondary'} className="text-xs px-1.5 py-0.5 mt-1.5">
                        {daySchedules.length}
                      </Badge>
                    </div>
                  </TabsTrigger>
                );
              })}
            </TabsList>

            {/* 선택된 날짜의 스케줄 */}
            {dates.map((date) => (
              <TabsContent key={date} value={date} className="mt-0">
                <div className="bg-card rounded-lg shadow-sm p-4 border">
                  <h3 className="text-sm font-semibold mb-3">
                    {formatDateLabel(date)}의 일정 ({getSchedulesForDate(date).length}개)
                  </h3>
                  {getSchedulesForDate(date).length === 0 ? (
                    <p className="text-sm text-muted-foreground/60 text-center py-8">일정이 없습니다</p>
                  ) : (
                    getSchedulesForDate(date).map(renderScheduleCard)
                  )}
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </main>

      {/* 스케줄 상세 Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
          {selectedSchedule && (
            <>
              <DialogHeader>
                {/* 레이드 타이틀 + 수정/삭제 버튼 */}
                <div className="flex items-center gap-2">
                  <DialogTitle className="text-xl">{selectedSchedule.raidTitle}</DialogTitle>
                  {/* 방장 전용: 수정/삭제 버튼 */}
                  {selectedSchedule.mySchedule === 1 && (
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          setEditScheduleId(selectedSchedule.scheduleId);
                          setEditModalOpen(true);
                        }}
                        className="h-8 w-8"
                      >
                        <Edit className="h-4 w-4" />
                        <span className="sr-only">수정</span>
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={handleDelete}
                        className="h-8 w-8 text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                        <span className="sr-only">삭제</span>
                      </Button>
                    </div>
                  )}
                </div>

                <DialogDescription className="sr-only">
                  레이드 일정 상세 정보 및 참여자 목록
                </DialogDescription>

                {/* 스케줄 정보 */}
                <div className="flex flex-col md:flex-row md:items-center gap-1.5 md:gap-2 text-sm text-muted-foreground mt-2">
                  <div className="flex items-center gap-1.5">
                    <Clock className="w-4 h-4 flex-shrink-0" />
                    <span>{formatDateLabel(selectedSchedule.startTime.split('T')[0])} {formatTime(selectedSchedule.startTime)}</span>
                  </div>
                  <span className="hidden md:inline text-muted-foreground/50">|</span>
                  <div className="flex items-center gap-1.5">
                    <User className="w-4 h-4 flex-shrink-0" />
                    <span>생성자: {selectedSchedule.nickname}</span>
                  </div>
                </div>
              </DialogHeader>

              <div className="flex-1 overflow-y-auto space-y-4">
                {/* 메모 */}
                {selectedSchedule.content && (
                  <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                    <p className="text-sm font-semibold text-amber-900 dark:text-amber-100 mb-1">📝 메모</p>
                    <p className="text-sm text-amber-800 dark:text-amber-200">{selectedSchedule.content}</p>
                  </div>
                )}

                {/* 참여자 목록 */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-lg">참여자 목록</h3>
                    <Badge variant="outline">
                      {scheduleMembers?.length || 0}/{selectedSchedule.atkNum + selectedSchedule.supNum}명
                    </Badge>
                  </div>

                  {/* 색상 안내 */}
                  <div className="flex items-center gap-3 text-xs text-muted-foreground bg-muted/50 rounded-lg p-2">
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                      <span>딜러</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                      <span>서포터</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                      <span>둘다 가능</span>
                    </div>
                  </div>

                  {loadingMembers ? (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                      <p className="text-sm text-muted-foreground">로딩 중...</p>
                    </div>
                  ) : !scheduleMembers || scheduleMembers.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground/60">
                      <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">아직 참여자가 없습니다</p>
                    </div>
                  ) : (
                    <>
                      {/* 딜러 */}
                      {(() => {
                        const attackers = scheduleMembers.filter(
                          (m) => m.classType === 'attacker'
                        );
                        return attackers.length > 0 && (
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <Swords className="w-4 h-4 text-red-600" />
                              <span className="font-semibold text-sm">딜러</span>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                              {attackers.map((member) => {
                                const isMe = member.nickname === user?.memberNickname;
                                const isFlex = member.supPossible === 1; // 둘다 가능

                                return (
                                  <div
                                    key={member.id}
                                    className={`flex items-center justify-between p-3 rounded-lg ${
                                      isFlex
                                        ? 'bg-green-50 dark:bg-green-950/30'
                                        : 'bg-red-50 dark:bg-red-950/30'
                                    } ${
                                      isMe
                                        ? isFlex
                                          ? 'border-2 border-green-600 dark:border-green-500 ring-2 ring-green-300 dark:ring-green-800'
                                          : 'border-2 border-red-600 dark:border-red-500 ring-2 ring-red-300 dark:ring-red-800'
                                        : isFlex
                                          ? 'border border-green-200 dark:border-green-900/50'
                                          : 'border border-red-200 dark:border-red-900/50'
                                    }`}
                                  >
                                    <div className="flex items-center gap-3">
                                      <ClassIcon
                                        characterClass={member.characterClass}
                                        classType={member.classType as 'attacker' | 'supporter'}
                                        supPossible={member.supPossible}
                                        atkPossible={member.atkPossible}
                                        size={40}
                                      />
                                      <div>
                                        <div className="flex items-center gap-2">
                                          <p className="font-semibold text-sm">{member.nickname}</p>
                                          {isMe && <Badge className="text-xs px-1.5 py-0">나</Badge>}
                                        </div>
                                        <p className="text-xs text-muted-foreground">{member.characterClass}</p>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      {isAdmin && (
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleForceDelete(member.id);
                                          }}
                                          className="h-8 w-8 text-destructive hover:text-destructive"
                                        >
                                          <X className="h-4 w-4" />
                                        </Button>
                                      )}
                                      <Badge variant="secondary" className="text-xs">
                                        Lv.{member.itemLevel}
                                      </Badge>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })()}

                      {/* 서포터 */}
                      {(() => {
                        const supporters = scheduleMembers.filter(
                          (m) => m.classType === 'supporter'
                        );
                        return supporters.length > 0 && (
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <Shield className="w-4 h-4 text-blue-600" />
                              <span className="font-semibold text-sm">서포터</span>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                              {supporters.map((member) => {
                                const isMe = member.nickname === user?.memberNickname;
                                const isFlex = member.atkPossible === 1; // 둘다 가능

                                return (
                                  <div
                                    key={member.id}
                                    className={`flex items-center justify-between p-3 rounded-lg ${
                                      isFlex
                                        ? 'bg-green-50 dark:bg-green-950/30'
                                        : 'bg-blue-50 dark:bg-blue-950/30'
                                    } ${
                                      isMe
                                        ? isFlex
                                          ? 'border-2 border-green-600 dark:border-green-500 ring-2 ring-green-300 dark:ring-green-800'
                                          : 'border-2 border-blue-600 dark:border-blue-500 ring-2 ring-blue-300 dark:ring-blue-800'
                                        : isFlex
                                          ? 'border border-green-200 dark:border-green-900/50'
                                          : 'border border-blue-200 dark:border-blue-900/50'
                                    }`}
                                  >
                                    <div className="flex items-center gap-3">
                                      <ClassIcon
                                        characterClass={member.characterClass}
                                        classType={member.classType as 'attacker' | 'supporter'}
                                        supPossible={member.supPossible}
                                        atkPossible={member.atkPossible}
                                        size={40}
                                      />
                                      <div>
                                        <div className="flex items-center gap-2">
                                          <p className="font-semibold text-sm">{member.nickname}</p>
                                          {isMe && <Badge className="text-xs px-1.5 py-0">나</Badge>}
                                        </div>
                                        <p className="text-xs text-muted-foreground">{member.characterClass}</p>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      {isAdmin && (
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleForceDelete(member.id);
                                          }}
                                          className="h-8 w-8 text-destructive hover:text-destructive"
                                        >
                                          <X className="h-4 w-4" />
                                        </Button>
                                      )}
                                      <Badge variant="secondary" className="text-xs">
                                        Lv.{member.itemLevel}
                                      </Badge>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })()}
                    </>
                  )}
                </div>
              </div>

              {/* 액션 버튼 */}
              <div className="flex gap-2 pt-4 border-t mt-4">
                {selectedSchedule.isEnrolled === 1 ? (
                  <>
                    <Button
                      variant="default"
                      className="flex-1"
                      onClick={handleChangeCharacter}
                    >
                      캐릭터 변경
                    </Button>
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={handleCancelEnrollment}
                    >
                      참여 취소
                    </Button>
                  </>
                ) : (
                  <Button
                    className="w-full"
                    onClick={() => {
                      setIsEnrollUpdate(false);
                      setEnrollModalOpen(true);
                    }}
                    disabled={selectedSchedule.enrollMemberNum >= selectedSchedule.atkNum + selectedSchedule.supNum}
                  >
                    {selectedSchedule.enrollMemberNum >= selectedSchedule.atkNum + selectedSchedule.supNum
                      ? '인원 마감'
                      : '참여하기'}
                  </Button>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* 스케줄 생성 Modal */}
      <ScheduleCreateModal
        open={createModalOpen}
        onOpenChange={setCreateModalOpen}
        onSuccess={loadSchedules}
      />

      {/* 스케줄 수정 Modal */}
      <ScheduleEditModal
        open={editModalOpen}
        onOpenChange={setEditModalOpen}
        scheduleId={editScheduleId}
        onSuccess={() => {
          loadSchedules(true);
          if (selectedSchedule) {
            loadScheduleMembers(selectedSchedule.scheduleId);
          }
        }}
      />

      {/* 스케줄 참여 Modal */}
      <ScheduleEnrollModal
        open={enrollModalOpen}
        onOpenChange={(open) => {
          setEnrollModalOpen(open);
          if (!open) {
            setIsEnrollUpdate(false); // 모달 닫힐 때 업데이트 모드 리셋
          }
        }}
        schedule={selectedSchedule}
        schedules={schedules}
        characters={characters}
        charactersLoading={charactersLoading}
        isUpdate={isEnrollUpdate}
        onSuccess={async () => {
          // 스케줄 목록 다시 로드하고 업데이트된 데이터 받기
          const updatedSchedules = await loadSchedules(true);

          // 현재 선택된 스케줄을 업데이트된 데이터로 교체
          if (selectedSchedule) {
            const updatedSchedule = updatedSchedules.find(
              (s) => s.scheduleId === selectedSchedule.scheduleId
            );
            if (updatedSchedule) {
              setSelectedSchedule(updatedSchedule);
            }
            loadScheduleMembers(selectedSchedule.scheduleId);
          }
        }}
      />

      {/* 내 참여 현황 Dialog */}
      <MyParticipationDialog
        open={participationDialogOpen}
        onOpenChange={setParticipationDialogOpen}
        schedules={schedules}
        characters={characters}
      />

      {/* 내 정보 Dialog (Desktop) */}
      <UserProfileDialog
        open={userProfileDialogOpen}
        onOpenChange={setUserProfileDialogOpen}
        characters={characters}
      />

      {/* Floating Action Button (FAB) - 일정 생성 */}
      <Button
        onClick={() => setCreateModalOpen(true)}
        className="fixed bottom-6 right-6 h-14 w-14 rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-110 z-20"
        size="icon"
        aria-label="일정 생성"
      >
        <CalendarPlus className="h-6 w-6" />
      </Button>
    </div>
  );
}
